import { Sentry, logger, startSpan } from '../../lib/sentry'
import { supabase } from '../../lib/supabase'

import { FailedJobAttempt, SearchPlatform, SearchQuery } from './types'

// Handle viewing failed jobs
export async function handleViewFailedJobs(
  queryId: string,
  queryName: string,
  setFailedJobsModal: React.Dispatch<React.SetStateAction<{ queryId: string; queryName: string } | null>>,
  setLoadingFailedJobs: React.Dispatch<React.SetStateAction<boolean>>,
  setFailedJobs: React.Dispatch<React.SetStateAction<FailedJobAttempt[]>>
) {
  setFailedJobsModal({ queryId, queryName })
  setLoadingFailedJobs(true)
  try {
    const data = await startSpan(
      {
        op: 'db.query',
        name: `GET /failed_job_attempts?searchQueryId=${queryId}`,
      },
      async (span: { setAttribute?: (key: string, value: string | number) => void } | null) => {
        if (span) {
          span.setAttribute('query_id', queryId)
        }
        
        const { data, error } = await supabase
          .from('failed_job_attempts')
          .select('id, linkedin_job_id, linkedin_job_url, failure_reason, error_type, error_category, error_details, attempted_at')
          .eq('search_query_id', queryId)
          .order('attempted_at', { ascending: false })

        if (error) {
          logger.error('Error loading failed jobs', {
            error: error.message,
            code: error.code,
            queryId,
          } as Record<string, unknown>)
          Sentry.captureException(new Error(error.message), {
            extra: { code: error.code, queryId },
          })
          if (span) {
            span.setAttribute('error', true)
            span.setAttribute('error_message', error.message)
          }
          return null
        }
        
        if (span) {
          span.setAttribute('failed_jobs_count', data?.length || 0)
        }
        if (data) {
          logger.info(`Loaded ${data.length} failed jobs for query ${queryId}`, {
            queryId,
            count: data.length,
          } as Record<string, unknown>)
        }
        return data
      }
    )
    
    if (data) {
      setFailedJobs(data)
    } else {
      setFailedJobs([])
    }
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : String(err)
    logger.error('Unexpected error loading failed jobs', { error: errorMessage, queryId } as Record<string, unknown>)
    if (err instanceof Error) {
      Sentry.captureException(err)
    }
    setFailedJobs([])
  } finally {
    setLoadingFailedJobs(false)
  }
}

// Handle deleting a query
export async function handleDeleteQuery(
  id: string,
  queries: SearchQuery[],
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setQueries: React.Dispatch<React.SetStateAction<SearchQuery[]>>
) {
  const query = queries.find(q => q.id === id)
  const jobsCount = query?.scraped_jobs_count || 0
  
  const message = jobsCount > 0
    ? `Are you sure you want to delete this search query? This will also delete ${jobsCount} associated job${jobsCount !== 1 ? 's' : ''}.`
    : 'Are you sure you want to delete this search query?'
  
  if (!confirm(message)) {
    return
  }

  const { error: deleteError } = await supabase
    .from('search_queries')
    .delete()
    .eq('id', id)

  if (deleteError) {
    setError(deleteError.message)
    return
  }

  setQueries(prev => prev.filter(q => q.id !== id))
}

// Handle adding a new query
export async function handleAddQuery(
  e: React.FormEvent,
  formData: { name: string; search_url: string; platform: SearchPlatform },
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setQueries: React.Dispatch<React.SetStateAction<SearchQuery[]>>,
  setFormData: React.Dispatch<React.SetStateAction<{ name: string; search_url: string; platform: SearchPlatform }>>,
  setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>
) {
  e.preventDefault()
  setError(null)

  if (!formData.name.trim() || !formData.search_url.trim()) {
    setError('Please fill in all required fields')
    return
  }

  await startSpan(
    {
      op: 'db.insert',
      name: 'POST /search_queries',
    },
    async (span: { setAttribute?: (key: string, value: string | number) => void } | null) => {
      try {
        if (span) {
          span.setAttribute('platform', formData.platform)
          span.setAttribute('name', formData.name.trim())
        }

        const { data, error: insertError } = await supabase
          .from('search_queries')
          .insert({
            name: formData.name.trim(),
            search_url: formData.search_url.trim(),
            platform: formData.platform
          })
          .select()
          .single()

        if (insertError) {
          logger.error('Failed to create search query', {
            error: insertError.message,
            code: insertError.code,
            platform: formData.platform,
          } as Record<string, unknown>)
          Sentry.captureException(new Error(insertError.message), {
            extra: { code: insertError.code, platform: formData.platform },
          })
          if (span) {
            span.setAttribute('error', true)
            span.setAttribute('error_message', insertError.message)
          }
          setError(insertError.message)
          return
        }

        if (data) {
          if (span) {
            span.setAttribute('query_id', data.id)
          }
          logger.info('Search query created', { queryId: data.id, name: data.name, platform: data.platform } as Record<string, unknown>)

          const { count } = await supabase
            .from('failed_job_attempts')
            .select('*', { count: 'exact', head: true })
            .eq('search_query_id', data.id)
          
          setQueries((prev) => [{ ...data, failed_jobs_count: count || 0 }, ...prev])
          setFormData({ name: '', search_url: '', platform: 'linkedin' })
          setIsDrawerOpen(false)
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : String(err)
        logger.error('Unexpected error creating search query', { error: errorMessage } as Record<string, unknown>)
        if (err instanceof Error) {
          Sentry.captureException(err)
        }
        setError(errorMessage || 'Failed to create search query')
      }
    }
  )
}

// Handle sorting
export function handleSort(
  column: string,
  sortColumn: string | null,
  sortDirection: 'asc' | 'desc',
  setSortColumn: React.Dispatch<React.SetStateAction<string | null>>,
  setSortDirection: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>
) {
  if (sortColumn === column) {
    // Toggle direction if same column
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
  } else {
    // New column, start with ascending
    setSortColumn(column)
    setSortDirection('asc')
  }
}

// Handle row click for navigation
export function handleRowClick(query: SearchQuery) {
  window.location.href = `/?searchQueryId=${query.id}`
}
